import React, { useMemo } from 'react';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import ReceptionModalShell, { ReceptionModalSection, ReceptionFormField } from '../ui/ReceptionModalShell';
import {
  ReceptionStatusBadge,
  ReceptionSearchAnchor,
  ReceptionFloatingResults,
  ReceptionFloatingResultItem
} from '../ui';

const RDV_TYPES = [
  { value: 'MEDECIN', label: 'Médecin', service: 'CONSULTATION_MEDECIN' },
  { value: 'DENTISTE', label: 'Dentiste', service: 'CONSULTATION_DENTAIRE' },
  { value: 'INFIRMERIE', label: 'Infirmerie', service: 'SERVICE_INFIRMERIE' },
  { value: 'AUTRE_SERVICE', label: 'Autre service', service: 'AUTRE_SERVICE_MEDICAL' }
];

const TIME_SLOTS = ['08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '14:00', '14:30', '15:00', '15:30', '16:00'];

function serviceForType(type) {
  return RDV_TYPES.find((t) => t.value === type)?.service || 'AUTRE_SERVICE_MEDICAL';
}

function formatSummaryDate(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  try {
    const d = new Date(`${dateStr}T${timeStr}`);
    if (Number.isNaN(d.getTime())) return `${dateStr} ${timeStr}`;
    return d.toLocaleString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return `${dateStr} ${timeStr}`;
  }
}

export default function RendezVousFormModal({
  show,
  onHide,
  saving = false,
  onSubmit,
  form,
  setForm,
  patientSearch,
  onPatientSearchChange,
  patientOptions = [],
  patientLoading = false,
  doctors = [],
  availability = null,
  mapServiceOnTypeChange = true,
  mode = 'create',
  editingReference = '',
  lockPatient = false,
  patientDisplayName = ''
}) {
  const selectedPatientLabel = useMemo(() => {
    if (!form.patient_id) return null;
    const fromPicker = patientOptions.find((o) => String(o.id) === String(form.patient_id));
    if (fromPicker) return fromPicker.label;
    return patientSearch.trim() || null;
  }, [form.patient_id, patientOptions, patientSearch]);

  const selectedDoctorName = useMemo(() => {
    if (!form.doctor_id) return null;
    const d = doctors.find((doc) => String(doc.id) === String(form.doctor_id));
    if (!d) return null;
    return `${d.first_name || ''} ${d.last_name || ''}`.trim();
  }, [form.doctor_id, doctors]);

  const showPractitionerBlock =
    form.type_rendez_vous === 'MEDECIN' || form.type_rendez_vous === 'DENTISTE';

  const handleTypeChange = (typeValue) => {
    setForm((prev) => ({
      ...prev,
      type_rendez_vous: typeValue,
      doctor_id: '',
      doctor_preference: 'SANS_PREFERENCE',
      ...(mapServiceOnTypeChange ? { service: serviceForType(typeValue) } : {})
    }));
  };

  const isEdit = mode === 'edit';

  const footer = (
    <>
      <Button variant="outline-secondary" className="recv-btn-ghost" onClick={onHide} disabled={saving}>
        Annuler
      </Button>
      <Button variant="primary" onClick={onSubmit} disabled={saving}>
        {saving ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" aria-hidden />
            {isEdit ? 'Enregistrement…' : 'Création…'}
          </>
        ) : isEdit ? (
          'Enregistrer les modifications'
        ) : (
          'Créer le rendez-vous'
        )}
      </Button>
    </>
  );

  return (
    <ReceptionModalShell
      show={show}
      onHide={onHide}
      disableClose={saving}
      size="xl"
      dialogClassName="recv-modal-dialog--rdv"
      title={isEdit ? `Modifier le rendez-vous${editingReference ? ` ${editingReference}` : ''}` : 'Nouveau rendez-vous'}
      subtitle={
        isEdit
          ? 'Ajustez le créneau, le service ou les notes. Le patient reste inchangé.'
          : 'Planifiez un créneau : patient, type de soin, praticien et horaire.'
      }
      footer={footer}
    >
      <Form onSubmit={(e) => e.preventDefault()} className="recv-form-modern">
        <Row className="g-4">
          <Col lg={8}>
            <ReceptionModalSection
              title="Type de consultation"
              description="Choisissez le service concerné."
            >
              <div className="recv-type-pills mb-3" role="group" aria-label="Type de rendez-vous">
                {RDV_TYPES.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    className={`recv-type-pill ${form.type_rendez_vous === value ? 'is-active' : ''}`}
                    onClick={() => handleTypeChange(value)}
                    aria-pressed={form.type_rendez_vous === value}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <Row className="g-3">
                <Col md={6}>
                  <ReceptionFormField label="Service" htmlFor="rdv-service" required>
                    <Form.Control
                      id="rdv-service"
                      value={form.service}
                      onChange={(e) => setForm((prev) => ({ ...prev, service: e.target.value }))}
                      placeholder="Ex. Consultation médecin"
                      required
                    />
                  </ReceptionFormField>
                </Col>
                <Col md={6}>
                  <ReceptionFormField label="Priorité" htmlFor="rdv-priority">
                    <Form.Select
                      id="rdv-priority"
                      value={form.priority}
                      onChange={(e) => setForm((prev) => ({ ...prev, priority: e.target.value }))}
                    >
                      <option value="NORMALE">Normale</option>
                      <option value="URGENT">Urgent</option>
                    </Form.Select>
                  </ReceptionFormField>
                </Col>
              </Row>
            </ReceptionModalSection>

            <ReceptionModalSection
              title="Patient"
              description={
                lockPatient
                  ? 'Le patient ne peut pas être modifié depuis cette fiche.'
                  : 'Recherchez par nom, prénom ou matricule (min. 2 caractères).'
              }
            >
              {lockPatient ? (
                <div className="recv-rdv-form-patient-lock">
                  <div className="recv-rdv-form-patient-lock__name">{patientDisplayName || 'Patient'}</div>
                  <div className="recv-rdv-form-patient-lock__kind">
                    {form.patient_kind === 'SMIE' ? 'Adhérent SMIE' : 'Membre externe'}
                  </div>
                </div>
              ) : (
              <Row className="g-3">
                <Col md={4}>
                  <ReceptionFormField label="Catégorie" htmlFor="rdv-patient-kind" required>
                    <Form.Select
                      id="rdv-patient-kind"
                      value={form.patient_kind}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, patient_kind: e.target.value, patient_id: '' }))
                      }
                    >
                      <option value="SMIE">SMIE / ayant droit</option>
                      <option value="EXTERNE">Membre externe</option>
                    </Form.Select>
                  </ReceptionFormField>
                </Col>
                <Col md={8}>
                  <ReceptionFormField
                    label="Recherche patient"
                    htmlFor="rdv-patient-search"
                    required
                    hint={
                      patientSearch.trim().length > 0 && patientSearch.trim().length < 2
                        ? 'Saisissez au moins 2 caractères'
                        : undefined
                    }
                  >
                    <ReceptionSearchAnchor>
                      <Form.Control
                        id="rdv-patient-search"
                        type="search"
                        value={patientSearch}
                        onChange={(e) => onPatientSearchChange(e.target.value)}
                        placeholder="Nom, prénom ou matricule…"
                        autoComplete="off"
                        aria-autocomplete="list"
                        aria-controls="rdv-patient-list"
                        aria-expanded={patientOptions.length > 0 || patientLoading}
                      />
                      <ReceptionFloatingResults
                        listId="rdv-patient-list"
                        show={patientSearch.trim().length >= 2}
                        loading={patientLoading}
                        emptyMessage="Aucun patient trouvé"
                      >
                        {patientOptions.map((opt) => (
                          <ReceptionFloatingResultItem
                            key={`${opt.sub || 'p'}-${opt.id}`}
                            primary={opt.label}
                            meta={opt.sub}
                            actionLabel="Choisir"
                            selected={String(form.patient_id) === String(opt.id)}
                            onSelect={() => onPatientSearchChange(opt.label, String(opt.id))}
                          />
                        ))}
                      </ReceptionFloatingResults>
                    </ReceptionSearchAnchor>
                  </ReceptionFormField>
                </Col>
              </Row>
              )}
            </ReceptionModalSection>

            {showPractitionerBlock && (
              <ReceptionModalSection title="Praticien" description="Optionnel : file d'attente si sans préférence.">
                <Row className="g-3">
                  <Col md={6}>
                    <ReceptionFormField label="Préférence" htmlFor="rdv-doc-pref">
                      <Form.Select
                        id="rdv-doc-pref"
                        value={form.doctor_preference}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            doctor_preference: e.target.value,
                            doctor_id: e.target.value === 'SPECIFIQUE' ? prev.doctor_id : ''
                          }))
                        }
                      >
                        <option value="SANS_PREFERENCE">Sans préférence (file commune)</option>
                        <option value="SPECIFIQUE">Praticien spécifique</option>
                      </Form.Select>
                    </ReceptionFormField>
                  </Col>
                  {form.doctor_preference === 'SPECIFIQUE' && (
                    <Col md={6}>
                      <ReceptionFormField
                        label={form.type_rendez_vous === 'DENTISTE' ? 'Dentiste' : 'Médecin'}
                        htmlFor="rdv-doctor"
                        required
                      >
                        <Form.Select
                          id="rdv-doctor"
                          value={form.doctor_id}
                          onChange={(e) => setForm((prev) => ({ ...prev, doctor_id: e.target.value }))}
                          required
                        >
                          <option value="">Sélectionner un praticien…</option>
                          {doctors
                            .filter((d) =>
                              form.type_rendez_vous === 'DENTISTE'
                                ? d.role === 'DENTIST'
                                : d.role === 'DOCTOR' || d.role === 'HEAD_DOCTOR'
                            )
                            .map((d) => (
                              <option key={d.id} value={d.id}>
                                {`${d.first_name || ''} ${d.last_name || ''}`.trim()}
                              </option>
                            ))}
                        </Form.Select>
                      </ReceptionFormField>
                    </Col>
                  )}
                </Row>
              </ReceptionModalSection>
            )}

            <ReceptionModalSection title="Date &amp; horaire" description="Créneau de début obligatoire.">
              <Row className="g-3 mb-2">
                <Col sm={6} md={4}>
                  <ReceptionFormField label="Date" htmlFor="rdv-date" required>
                    <Form.Control
                      id="rdv-date"
                      type="date"
                      value={form.date_rdv}
                      onChange={(e) => setForm((prev) => ({ ...prev, date_rdv: e.target.value }))}
                      required
                    />
                  </ReceptionFormField>
                </Col>
                <Col sm={6} md={4}>
                  <ReceptionFormField label="Heure début" htmlFor="rdv-time-start" required>
                    <Form.Control
                      id="rdv-time-start"
                      type="time"
                      value={form.heure_debut}
                      onChange={(e) => setForm((prev) => ({ ...prev, heure_debut: e.target.value }))}
                      required
                    />
                  </ReceptionFormField>
                </Col>
                <Col sm={6} md={4}>
                  <ReceptionFormField label="Heure fin" htmlFor="rdv-time-end" hint="Optionnel">
                    <Form.Control
                      id="rdv-time-end"
                      type="time"
                      value={form.heure_fin}
                      onChange={(e) => setForm((prev) => ({ ...prev, heure_fin: e.target.value }))}
                    />
                  </ReceptionFormField>
                </Col>
              </Row>
              {form.date_rdv && (
                <div className="recv-time-slots">
                  <span className="recv-time-slots__label">Créneaux rapides</span>
                  <div className="recv-time-slots__grid" role="group" aria-label="Sélection rapide de l'heure">
                    {TIME_SLOTS.map((slot) => (
                      <button
                        key={slot}
                        type="button"
                        className={`recv-time-slot ${form.heure_debut === slot ? 'is-active' : ''}`}
                        onClick={() => setForm((prev) => ({ ...prev, heure_debut: slot }))}
                        aria-pressed={form.heure_debut === slot}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <Row className="g-3 mt-1">
                <Col md={6}>
                  <ReceptionFormField label="Date fin" htmlFor="rdv-date-end" hint="Si créneau sur plusieurs jours">
                    <Form.Control
                      id="rdv-date-end"
                      type="date"
                      value={form.date_fin}
                      onChange={(e) => setForm((prev) => ({ ...prev, date_fin: e.target.value }))}
                    />
                  </ReceptionFormField>
                </Col>
              </Row>
            </ReceptionModalSection>

            <ReceptionModalSection title="Motif &amp; notes">
              <Row className="g-3">
                <Col xs={12}>
                  <ReceptionFormField label="Motif" htmlFor="rdv-motif">
                    <Form.Control
                      id="rdv-motif"
                      value={form.motif}
                      onChange={(e) => setForm((prev) => ({ ...prev, motif: e.target.value }))}
                      placeholder="Motif de consultation"
                    />
                  </ReceptionFormField>
                </Col>
                <Col xs={12}>
                  <ReceptionFormField label="Note interne" htmlFor="rdv-note" hint="Visible uniquement par le personnel">
                    <Form.Control
                      id="rdv-note"
                      as="textarea"
                      rows={3}
                      value={form.note_interne}
                      onChange={(e) => setForm((prev) => ({ ...prev, note_interne: e.target.value }))}
                      placeholder="Informations utiles pour l'accueil ou le soignant"
                    />
                  </ReceptionFormField>
                </Col>
              </Row>
            </ReceptionModalSection>

            {availability && (
              <Alert
                variant={availability.available ? 'success' : 'warning'}
                className="recv-modal-alert mb-0"
              >
                {availability.available
                  ? 'Créneau disponible pour ce praticien.'
                  : 'Conflit détecté sur ce créneau pour le praticien sélectionné.'}
              </Alert>
            )}
          </Col>

          <Col lg={4}>
            <aside className="recv-modal-summary" aria-label="Résumé du rendez-vous">
              <h3 className="recv-modal-summary__title">Résumé</h3>
              <ul className="recv-modal-summary__list">
                <li>
                  <span className="recv-modal-summary__key">Type</span>
                  <ReceptionStatusBadge kind="rdvType" value={form.type_rendez_vous} />
                </li>
                <li>
                  <span className="recv-modal-summary__key">Patient</span>
                  <span className="recv-modal-summary__val">
                    {selectedPatientLabel || (
                      <span className="text-muted">À sélectionner</span>
                    )}
                  </span>
                </li>
                <li>
                  <span className="recv-modal-summary__key">Créneau</span>
                  <span className="recv-modal-summary__val">
                    {formatSummaryDate(form.date_rdv, form.heure_debut) || (
                      <span className="text-muted">Date et heure requises</span>
                    )}
                  </span>
                </li>
                <li>
                  <span className="recv-modal-summary__key">Priorité</span>
                  <ReceptionStatusBadge kind="priority" value={form.priority || 'NORMALE'} />
                </li>
                {showPractitionerBlock && form.doctor_preference === 'SPECIFIQUE' && (
                  <li>
                    <span className="recv-modal-summary__key">Praticien</span>
                    <span className="recv-modal-summary__val">
                      {selectedDoctorName || <span className="text-muted">Non choisi</span>}
                    </span>
                  </li>
                )}
                {form.motif?.trim() && (
                  <li>
                    <span className="recv-modal-summary__key">Motif</span>
                    <span className="recv-modal-summary__val">{form.motif.trim()}</span>
                  </li>
                )}
              </ul>
              <p className="recv-modal-summary__hint small text-muted mb-0">
                {isEdit
                  ? 'Vérifiez le résumé avant d’enregistrer les modifications.'
                  : 'Vérifiez le résumé avant de confirmer la création.'}
              </p>
            </aside>
          </Col>
        </Row>
      </Form>
    </ReceptionModalShell>
  );
}
